import type { FlowContinuationId } from '../../../app-core/lib/flow/FlowReport'
import {
    isFlowWorkflow,
    type AppActivityWorkflow,
    type AppTrainingWorkflow,
} from '../../../app-core/lib/workflow/Workflow'
import type { ClientLogger } from '../../../shared/lib/ClientLogger.no-deps'
import type { UUID } from '../../../shared/lib/typeHelpers.no-deps'
import { Acti_cancelActivity } from '../Acti/Acti.cancelActivity'
import { Acti_startActivity } from '../Acti/Acti.startActivity'
import { Flow_reportStart } from '../Flow/Flow.Report'
import { IDB_flowReports } from '../IDB/IDB.dexie'
import { IDB_locationRead } from '../IDB/IDB.table.location'
import { Loca_getSingleLocation } from '../Loca/Loca.getSingleLocation'
import { Plan_addEvent } from '../Plan/Plan.addEvent'
import { Rout_toFlowExecutionPage } from '../Rout/Rout.flow'
import { Rout_toAdHocStart, Rout_toAdHocSummary } from '../Rout/Rout.todo'
import { Rpts_overrideOpenReportDialog } from '../Rpts/Rpts.ui.OpenReportWarning'
import ActivityStore from '../../src/stores/activity'
import { WF_fetch } from './WF.fetch'
import { WF_fetchMissingTrainingRecommendations } from './WF.fetchMissingTrainingRecommendations'

export const WF_startAdHocReport = async (
    logger: ClientLogger,
    workflowId: number,
    procedureId: number,
    opts?: {
        comingFromActionScheduledInstance?: boolean
        comingFromScheduledInstance?: UUID
        comingFromUnit?: number
        comingFromWorkflow?: number
        previousReport?: FlowContinuationId
        subjectUnitId?: number
    }
) => {
    const workflow = await WF_fetch(logger, workflowId, { includeDeleted: false })

    // For flow reports
    if (isFlowWorkflow(workflow)) {
        const missingTrainings = await WF_fetchMissingTrainingRecommendations(logger, workflow.id)

        // If we have missing training requirements then we need to route to the summary page.
        if (missingTrainings.length > 0) {
            logger.log('workflow has missing training requirements')
            Rout_toAdHocStart(procedureId, workflow.id, {
                previousReport: opts?.previousReport,
                unit: opts?.subjectUnitId,
            })
            return
        }

        // If we need to pick a subject then go to ad hoc summary page
        const subjectType = workflow.flow.subjectType
        if (subjectType != null && !opts?.subjectUnitId) {
            logger.log('need to pick unit subject')
            Rout_toAdHocSummary(procedureId, workflowId, { previousReport: opts?.previousReport })
            return
        }

        // Otherwise start the report
        await Flow_reportStart(logger, procedureId, workflow, opts)
        return
    }

    // Start report with activity workflow
    await WF_startAdHocActivityReport(logger, procedureId, workflow)
}

/**
 * Start an ad hoc report with an activity worklow
 */
export const WF_startAdHocActivityReport = async (
    logger: ClientLogger,
    procedureId: number,
    workflow: AppActivityWorkflow
) => {
    const locationId = await Loca_getSingleLocation()

    if (ActivityStore.hasCurrentActivity()) {
        // ask to override the current report
        const reportLocation = ActivityStore.getSelectedStore()
        const overrideReport = await Rpts_overrideOpenReportDialog(logger, reportLocation)
        if (!overrideReport) return

        await Acti_cancelActivity()
    }

    const event = await Plan_addEvent(logger, new Date(), locationId, workflow.id, procedureId)

    const location = await IDB_locationRead(locationId)

    // this navigates away from the page so it will cause the calling spinner/try-catch button
    // to warn that setState was called after it was unmounted, but that is ok
    return Acti_startActivity(logger, location, event.id)
}

export const WF_startAdHocTrainingReport = async (
    logger: ClientLogger,
    workflow: AppTrainingWorkflow,
    opts?: {
        comingFromActionScheduledInstance?: boolean
        comingFromScheduledInstance?: UUID
        comingFromUnit?: number
        comingFromWorkflow?: number
    }
) => {
    // Fetch existing open reports for this training workflow and sort by newest first.
    const openReportIds = await IDB_flowReports()
        .filter((report) => report.workflow.id === workflow.id && report.status === 'open' && !report.scheduledInstance)
        .toArray()
        .then((reports) =>
            reports.sort((a, b) => b.startDate.getTime() - a.startDate.getTime()).map((report) => report.id)
        )

    if (openReportIds.length > 0) {
        // Continue using existing newest open report.
        const reportId = openReportIds[0]
        logger.log(`navigating to open report`, { openGuideReportId: reportId, sectionId: workflow.flow.id })
        Rout_toFlowExecutionPage(reportId, { ...opts, sectionId: workflow.flow.id })
        return
    }

    // Ensure a procedure is associated with the workflow before we continue
    if (!workflow.procedure) {
        logger.error('Training workflow does not appear to be associated with a procedure: ', { workflow })
        return
    }

    // Start a report from scratch.
    const procedureId = workflow.procedure.id
    WF_startAdHocReport(logger, workflow.id, procedureId, opts)
}
